// Mirrors backend/apps/contractor_led/payloads.py — the key tuples there are the
// contract; anything not listed simply never reaches the browser.

export type LedSide = "gk" | "contractor";
export type EventSide = "gk" | "contractor" | "system";

export type TitleStatus =
  | "not_started"
  | "in_progress"
  | "secured"
  | "refused"
  | "not_required"
  | "problem"
  /** Pochodna zgłoszenia oczekującego na decyzję GK — tytuł w DLC 01 jeszcze się nie zmienił. */
  | "awaiting_confirmation";

export type TitleSummary = "not_started" | "in_progress" | "secured" | "problem";

export interface LedCounters {
  parcels_in_route: number;
  parcels_out_of_route: number;
  parcels_public: number;
  owners_total: number;
  /** Tytuły pozyskane NA BIEŻĄCEJ wersji trasy (plus strony działek uzgodnionych statusem). */
  owners_secured: number;
  /** Tytuły pozyskane dla innej wersji trasy niż bieżąca — nie uzgadniają działki. */
  owners_stale: number;
  tiles_total: number;
  tiles_done: number;
}

export interface LedTile {
  id: string;
  key: string;
  label: string;
  state: string;
  state_display: string;
  due_date: string | null;
  is_custom: boolean;
  side: LedSide | null;
  /** Tylko w odpowiedzi dla wewnętrznych. */
  assigned_order_number?: string | null;
  assigned_contractor_name?: string | null;
  internal_note?: string;
}

export interface LedRouteMeta {
  version_label: string;
  created_at: string | null;
  uploaded_by_label: string | null;
  length_m: number | null;
  /** Rysunek, z którego powstała wersja — do pobrania przez obie strony. */
  dxf: LedFile | null;
}

export interface LedSummaryInternal {
  caretaker_id: string | null;
  caretaker_full_name: string | null;
  handed_over_by_label: string | null;
  note: string;
}

export interface LedSummary {
  id: string;
  connection_id: string;
  project_id: string;
  name: string;
  short_name: string;
  client_name: string;
  branch_name: string;
  sub_branch_name: string;
  contractor_id: string;
  contractor_name: string;
  order_id: string | null;
  order_number: string | null;
  caretaker_label: string | null;
  handed_over_at: string;
  negotiator_mode: "lead_contractor" | "other";
  counters: LedCounters;
  route: LedRouteMeta | null;
  tiles: LedTile[];
  badges: LedBadges;
  internal?: LedSummaryInternal;
}

export interface LedOwner {
  id: string;
  kind: "person" | "company";
  full_name: string;
  address: string;
  share: string;
  pesel_nip: string;
  phone: string;
  email: string;
  marital_group: string;
  title_status: TitleStatus;
  title_status_display: string;
  title_basis: string | null;
  secured_at: string | null;
  /** Tytuł „pozyskany" dla innej wersji trasy niż bieżąca (albo projekt bez bieżącej trasy) —
   *  liczy się jak „w trakcie", dopóki GK go nie rozciągnie albo nie powstanie nowy tytuł. */
  title_stale: boolean;
  /** Wersja trasy, dla której tytuł pozyskano (np. „A1"). */
  title_route_version: string | null;
}

/** Co wisi nad działką po zmianie trasy (null = nic). */
export interface LedRouteChange {
  /** Flaga DLC 01 „zmiana trasy na działce — do weryfikacji" (działka miała już postęp). */
  review_needed: boolean;
  review_version: string | null;
  review_at: string | null;
  current_version: string | null;
  stale_titles: { owner_id: string; owner_name: string; title_version: string | null }[];
}

export interface LedParcel {
  id: string;
  teryt: string;
  short_number: string;
  obreb: string;
  commune: string;
  address: string;
  area_m2: number | null;
  intersection_length_m: number | null;
  in_current_route: boolean;
  is_public: boolean;
  location_category: string;
  kw_number: string;
  /** Status działki z warsztatu ZE (DLC 01) — „ok" = uzgodniona; `status_locked` = ustawiony
   *  ręcznie. `title_summary` szanuje ten status (uzgodniona → „secured" także bez tytułów). */
  status: string;
  status_display: string;
  status_locked: boolean;
  title_summary: TitleSummary;
  route_review_needed: boolean;
  route_change: LedRouteChange | null;
  /** Dlaczego działka ma taki status po zmianie trasy — tekst dla obu stron ("" = nic nie wisi). */
  status_reason: string;
  geometry: GeoJSON.Geometry | null;
  owners: LedOwner[];
}

export interface LedRouteObject {
  id: string;
  semantic_type: string;
  label: string;
  geometry: GeoJSON.Geometry;
}

export interface LedRouteLayers {
  /** Linie z typem (kabel / napowietrzna / SN / nN) — jak na rysunku projektanta. */
  typed_lines: { type: "FeatureCollection"; features: { type: "Feature"; geometry: GeoJSON.Geometry; properties: { semantic_type: string } }[] } | null;
  demolition: GeoJSON.Geometry | { type: "FeatureCollection"; features: unknown[] } | null;
  /** Urządzenia z DXF-a: słupy, stacje, złącza. */
  objects: LedRouteObject[];
}

export interface LedRouteVersionRow {
  id: string;
  label: string;
  created_at: string;
  author_label: string;
  comment: string;
  length_m: number | null;
  is_current: boolean;
  dxf: LedFile | null;
}

export interface LedParcelsPayload {
  route: GeoJSON.Geometry | null;
  route_layers: LedRouteLayers;
  route_history: LedRouteVersionRow[];
  parcels: LedParcel[];
}

export interface LedJournalRow {
  id: string;
  source: "project" | "led";
  kind: string;
  title: string;
  occurred_at: string;
  actor_label: string;
  side: EventSide;
  parcel_teryt: string | null;
  owner_name: string | null;
  recent: boolean;
  /** Tylko dla wewnętrznych. */
  detail?: string;
  actor_full_name?: string | null;
  internal_only?: boolean;
}

export interface LedJournalResult {
  rows: LedJournalRow[];
  days: number;
  recent: number;
  internal: boolean;
}

export interface MyLedProject {
  leadership_id: string;
  connection_id: string;
  name: string;
  short_name: string;
  order_id: string | null;
  order_number: string | null;
  handed_over_at: string;
  counters: LedCounters;
}

export interface HandOverForm {
  already_led: boolean;
  project_name: string;
  contractors: { id: string; name: string }[];
  caretakers: { id: string; name: string }[];
  default_caretaker: string;
  /** Termin z umowy przedsięwzięcia — propozycja terminu zamówienia i kafli. */
  default_deadline: string | null;
  tiles: LedTile[];
}

/** Pozycja zamówienia „Prowadzenie zlecenia": z cennika umowy ramowej albo ręczna. */
export interface LeadOrderLineInput {
  line_item_type?: string | null;
  custom_name?: string;
  custom_unit?: string;
  unit_rate?: string | null;
  quantity: string;
  description?: string;
}

export interface PriceListItem {
  id: string;
  name: string;
  unit: string;
  base_rate: string | null;
}

export interface HandOverTileInput {
  id?: string;
  label?: string;
  due_date?: string | null;
  /** Stan kafla — dialog przekazania może go poprawić (backend: MilestoneService). */
  state?: string | null;
  side?: LedSide | null;
  delete?: boolean;
}

export interface HandOverReportCycleInput {
  recipient: ReportRecipient;
  frequency: ReportFrequency;
  starts_on: string;
  weekday: number;
  day_of_month: number;
  label?: string;
}

export interface HandOverInput {
  contractor: string;
  caretaker?: string | null;
  deadline: string;
  line_items: LeadOrderLineInput[];
  /** Pierwszy cykl raportów — opcjonalny, ustawiany od razu przy przekazaniu. */
  report_cycle?: HandOverReportCycleInput | null;
  note: string;
  tiles: HandOverTileInput[];
}

export interface LedStatus {
  led: boolean;
  leadership: LedSummary | null;
}

export type ActiveLeadershipMap = Record<
  string,
  {
    leadership_id: string;
    contractor_name: string;
    caretaker_label: string | null;
    handed_over_at: string;
    /** Zgłoszenia tytułów od firmy czekające na NASZE potwierdzenie — udział projektu
     *  w plakietce „N tyt." z menu; portfel filtruje i liczy po tym polu. */
    titles_pending: number;
  }
>;

/** Słownik stanów tytułu — 1:1 z DLC 01 (OwnerAccessStatus), żeby to samo pole nie
 *  nazywało się w dwóch miejscach inaczej; „Czeka na potwierdzenie" istnieje tylko tutaj. */
export const TITLE_STATUS_LABEL: Record<TitleStatus, string> = {
  not_started: "Nie rozpoczęto",
  in_progress: "W trakcie",
  secured: "Pozyskany",
  refused: "Odmowa",
  not_required: "Niewymagany",
  problem: "Problem",
  awaiting_confirmation: "Czeka na potwierdzenie",
};

export const SIDE_LABEL: Record<LedSide, string> = {
  gk: "GK ELEKTIM",
  contractor: "Podwykonawca",
};

// ── etapy 2–4: sekcje robocze (payloads_work.py) ──────────────────────────────
export interface LedFile {
  id: string;
  name: string;
  size: number | null;
  url: string | null;
}

// ── zakładka „Pliki" (payloady files.py — kopia plików projektu + własne obu stron) ──
export interface LedFolderEntry {
  /** Id wiersza LedFile — cel PATCH / remove / hide / transfer. */
  id: string;
  managed_file_id: string;
  filename: string;
  url: string | null;
  size_bytes: number | null;
  created_at: string;
  description: string;
  side: EventSide;
  /** „GK ELEKTIM (J.K.)" albo „Firma (M.L.)" — jak w dzienniku. */
  uploaded_by_label: string;
  /** Kopia z chwili przekazania (origin_label = skąd plik pochodził w projekcie). */
  copied_at_handover: boolean;
  origin_label: string;
  /** Plik z zewnątrz bez czystego werdyktu skanera — url puste, czekamy. */
  scan_pending: boolean;
  /** Czy TEN widz może zmienić opis/nazwę, usunąć i przenieść plik. */
  can_manage: boolean;
  /** „Ukryj dla podwykonawcy" — u firmy zawsze false (ukrytych wpisów nie dostaje). */
  hidden_from_contractor: boolean;
  /** Tylko ELEKTIM. */
  origin_ref?: { kind: string; id: string; extra: Record<string, unknown> | null } | null;
  uploaded_by_full_name?: string | null;
  /** Tylko ELEKTIM: plik projektu ELEKTIM, którego w zakładce jeszcze NIE MA (czeka na
   *  „Dokopiuj"); id ma postać `pending:<slug>:<managed_file_id>`, bez zarządzania. */
  pending_copy?: boolean;
  /** Poufny w DLC 01 — po skopiowaniu będzie ukryty przed firmą automatycznie. */
  pending_confidential?: boolean;
  /** Źródło w DLC 01 (do otwarcia w Collaborze slotem `project_file`). */
  source_ref?: { kind: string; id: string; extra: Record<string, unknown> | null } | null;
  /** Dokument tytułu prawnego (projekcja z DLC 01, obie strony od razu, bez „Dokopiuj"): id ma
   *  postać `title:<slug>:<managed_file_id>`; bez zarządzania, podgląd slotem `led_title_file`,
   *  ukrycie przed firmą = kopia od razu ukryta (jak plik czekający na dokopiowanie). */
  title_document?: boolean;
  /** Załącznik kafla z zakładki „Etapy" (projekcja w folderze kamienia, obie strony widzą ten
   *  sam plik): id ma postać `tile:<tile_file_id>`; zarządza się nim w „Etapach", tu podgląd,
   *  pobranie i Collabora slotem `led_tile_file` (zapis wg `tile_can_edit`). */
  tile_file_id?: number;
  tile_milestone_id?: string;
  tile_milestone_label?: string;
  tile_can_edit?: boolean;
}

export interface LedFolderNode {
  slug: string;
  label: string;
  kind: "manual";
  is_custom: boolean;
  /** Zawsze false — po przekazaniu nie ma kłódek; pole zostaje dla parytetu z DLC_01. */
  read_only: boolean;
  source_hint: string;
  /** Własna flaga ukrycia / stan efektywny (odziedziczony z kontenera). U firmy false. */
  hidden_from_contractor: boolean;
  hidden_effective: boolean;
  /** Folder własny, którym ten widz zarządza (zmiana nazwy, usunięcie). */
  can_manage: boolean;
  file_count: number;
  entries: LedFolderEntry[];
  children: LedFolderNode[];
  /** Tylko ELEKTIM: folder własny DLC 01 założony po kopii — w zakładce pojawi się po dokopiowaniu. */
  pending_folder?: boolean;
}

export interface LedFilesSnapshot {
  done: boolean;
  at: string | null;
  copied_count: number;
  /** Ostatnie zadanie kopii w tle — po odświeżeniu widać, że poprzednia próba padła i dlaczego. */
  last_task?: {
    id: string;
    status: "pending" | "running" | "success" | "failure" | "cancelled" | string;
    error: string;
    created_at: string;
    finished_at: string | null;
  } | null;
}

export interface LedFilesPayload {
  folders: LedFolderNode[];
  internal: boolean;
  connection_id: string;
  contractor_name: string;
  /** Tylko ELEKTIM: ile plików projektu czeka na „Dokopiuj". */
  pending_count?: number;
  /** Tylko ELEKTIM: niepuste = lista nieskopiowanych NIEPEŁNA (drzewo plików projektu nie dało
   *  się odczytać; błąd w logach). */
  pending_error?: string;
  can_create_folder: boolean;
  can_hide: boolean;
  /** Tylko ELEKTIM: czy widz może uruchomić „Dokopiuj nowe pliki projektu" (koordynator / dyrektor / CEO). */
  can_resync?: boolean;
  /** Tylko ELEKTIM: stan kopii plików projektu z chwili przekazania. */
  snapshot?: LedFilesSnapshot;
  /** Doklejane do odpowiedzi konkretnych mutacji. */
  moved_to_inne?: number;
  transferred?: number;
  task_id?: string;
}

export interface LedBadges {
  memos_unread: number;
  register_unread: number;
  titles_pending: number;
  tile_requests_pending: number;
  reports_overdue: number;
  reports_due_today: number;
  /** Działki w trasie z flagą „zmiana trasy — do weryfikacji". */
  route_review: number;
}

export type EntryKind = "letter_out" | "letter_in" | "phone" | "visit" | "email" | "note";
export const ENTRY_KIND_LABEL: Record<EntryKind, string> = {
  letter_out: "Pismo wysłane",
  letter_in: "Pismo otrzymane",
  phone: "Telefon",
  visit: "Wizyta w terenie",
  email: "E-mail",
  note: "Notatka",
};

export interface LedEntry {
  id: string;
  kind: EntryKind;
  kind_display: string;
  occurred_on: string;
  body: string;
  files: LedFile[];
  author_label: string;
  side: EventSide;
  parcel_teryt: string | null;
  owner_id: string | null;
  owner_name: string | null;
  milestone_id: string | null;
  register_label: string | null;
  withdrawn: boolean;
  withdraw_reason: string;
  edited: boolean;
  created_at: string;
  can_edit: boolean;
}

export interface TitleReportRow {
  id: string;
  status: "pending" | "confirmed" | "rejected";
  basis_code: string;
  z7_consent_form: string;
  document_number: string;
  note: string;
  files: LedFile[];
  reported_by_label: string;
  reported_at: string;
  decided_at: string | null;
  decision_note: string;
  owner_id?: string;
  owner_name?: string;
  parcel_teryt?: string;
  parcel_id?: string;
}

export interface OwnerStatusChange {
  at: string;
  from_display: string | null;
  to_display: string;
  to: TitleStatus | "rejected";
  note: string;
  by_label: string;
}

export interface LedCardOwner extends LedOwner {
  institution_id: string | null;
  pending_report: TitleReportRow | null;
  /** Ostatnie zgłoszenie, jeśli zostało odrzucone — powód i plik, aż do nowego zgłoszenia. */
  rejected_report: TitleReportRow | null;
  /** Dokumenty bieżącego tytułu (OwnerAccess.files); u firmy bez ukrytych przed nią. */
  title_documents: LedFile[];
  entries: LedEntry[];
  /** Historia stanu tytułu (audyt DLC 01) — kto, kiedy, z czego na co. */
  status_history: OwnerStatusChange[];
  /** Pisma z rejestru spraw adresowane do tej strony. */
  letters: RegisterRow[];
}

export interface LedParcelCard {
  id: string;
  teryt: string;
  short_number: string;
  obreb: string;
  commune: string;
  address: string;
  area_m2: number | null;
  intersection_length_m: number | null;
  in_current_route: boolean;
  is_public: boolean;
  location_category: string;
  kw_number: string;
  status: string;
  status_display: string;
  status_locked: boolean;
  is_internal_road: boolean;
  road_note: string;
  title_summary: TitleSummary;
  route_review_needed: boolean;
  route_change: LedRouteChange | null;
  status_reason: string;
  /** Tylko GK: „Rozciągnij tytuł na aktualną wersję" i „Zmiana trasy zweryfikowana". */
  can_resolve_route_change?: boolean;
  /** Kosz przy dokumentach tytułu: koordynator, dyrektor, CEO — nigdy firma. */
  can_remove_title_documents: boolean;
  geometry: GeoJSON.Geometry | null;
  owners: LedCardOwner[];
  entries: LedEntry[];
  /** Pisma dotyczące całej działki (bez wskazanej strony). */
  letters: RegisterRow[];
}

export type InstitutionKind =
  | "gmina" | "starostwo" | "wojewodztwo" | "gddkia" | "pkp" | "lasy" | "wody" | "kowr" | "gestor" | "inny";
export const INSTITUTION_KIND_LABEL: Record<InstitutionKind, string> = {
  gmina: "Gmina / urząd miasta",
  starostwo: "Starostwo powiatowe",
  wojewodztwo: "Zarząd dróg wojewódzkich / urząd marszałkowski",
  gddkia: "GDDKiA",
  pkp: "PKP",
  lasy: "Lasy Państwowe",
  wody: "Wody Polskie",
  kowr: "KOWR",
  gestor: "Gestor sieci",
  inny: "Inna instytucja",
};

export interface LedInstitution {
  id: string;
  name: string;
  kind: InstitutionKind;
  kind_display: string;
  street: string;
  postal_code: string;
  city: string;
  phone: string;
  email: string;
  contact_person: string;
  note: string;
  is_active: boolean;
  parcels_count: number;
}

export interface InstitutionInput {
  name: string;
  kind: InstitutionKind;
  street?: string;
  postal_code?: string;
  city?: string;
  phone?: string;
  email?: string;
  contact_person?: string;
  note?: string;
}

export interface PersonInput {
  full_name: string;
  street?: string;
  building_no?: string;
  postal_code?: string;
  city?: string;
  pesel_nip?: string;
  phone?: string;
  email?: string;
  ownership_share?: string;
  own_share?: string;
  comment?: string;
}

export interface TitleOptions {
  bases: { code: string; label: string }[];
  consent_forms: { code: string; label: string }[];
  statuses: { code: TitleStatus; label: string }[];
}

export type RegisterDirection = "out" | "in";
export type RegisterKind =
  | "letter_to_office" | "letter_to_owner" | "response_office" | "response_owner" | "reminder";
export const REGISTER_KIND_LABEL: Record<RegisterKind, string> = {
  letter_to_office: "Pismo do urzędu / instytucji",
  letter_to_owner: "Pismo do właściciela",
  response_office: "Odpowiedź urzędu / instytucji",
  response_owner: "Odpowiedź właściciela",
  reminder: "Ponaglenie",
};

export interface RegisterRow {
  id: string;
  label: string;
  number: number;
  direction: RegisterDirection;
  direction_display: string;
  kind: RegisterKind;
  kind_display: string;
  entry_date: string;
  counterparty: string;
  institution_id: string | null;
  owner_id: string | null;
  owner_name: string | null;
  parcels: { id: string; teryt: string }[];
  reference: string;
  tracking_number: string;
  response_due: string | null;
  responds_to: string | null;
  responds_to_label: string | null;
  answered_on: string | null;
  delivered_on: string | null;
  body: string;
  files: LedFile[];
  author_label: string;
  side: EventSide;
  overdue: boolean;
  unread: boolean;
  prehandover: boolean;
  created_at: string;
  /** Tylko dla wewnętrznych. */
  hidden_from_contractor?: boolean;
}

export interface RegisterInput {
  direction: RegisterDirection;
  kind: RegisterKind;
  entry_date: string;
  counterparty?: string;
  institution?: string | null;
  owner?: string | null;
  parcels?: string[];
  reference?: string;
  tracking_number?: string;
  response_due?: string | null;
  responds_to?: string | null;
  delivered_on?: string | null;
  body?: string;
  files?: File[];
}

export interface TileFileRow extends LedFile {
  tile_file_id: number;
  uploaded_by_label: string;
  side: EventSide;
}

export interface WorkTile {
  id: string;
  key: string;
  label: string;
  state: string;
  state_display: string;
  due_date: string | null;
  completed_at: string | null;
  is_custom: boolean;
  side: LedSide | null;
  files: TileFileRow[];
  entries: LedEntry[];
  can_move: boolean;
  assigned_order_number?: string | null;
  assigned_order_id?: string | null;
  assigned_contractor_name?: string | null;
  assigned_contractor_id?: string | null;
  internal_note?: string;
}

export interface TileRequestRow {
  id: string;
  label: string;
  reason: string;
  status: "pending" | "accepted" | "rejected";
  status_display: string;
  requested_by_label: string;
  requested_at: string;
  decision_note: string;
  decided_at: string | null;
}

export interface TilesPayload {
  tiles: WorkTile[];
  requests: TileRequestRow[];
  states: { code: string; label: string }[];
}

export interface OrdersPicker {
  orders: { id: string; order_number: string; contractor_name: string; order_type_display: string; status_display: string }[];
  contractors: { id: string; name: string }[];
}

export interface MemoRow {
  id: string;
  label: string;
  number: number;
  subject: string;
  body: string;
  files: LedFile[];
  author_label: string;
  side: EventSide;
  sent_at: string;
  edited_at: string | null;
  versions_count: number;
  read_by_me: boolean;
  read_by: { label: string; read_at: string }[];
  parcels: { id: string; teryt: string }[];
  milestone_label: string | null;
  can_edit: boolean;
}

export type ReportRecipient = "client" | "gk";
export type ReportFrequency = "weekly" | "monthly";
export type ReportItemStatus = "forwarded" | "submitted" | "overdue" | "due_today" | "planned";

export interface ReportCycleRow {
  id: string;
  recipient: ReportRecipient;
  recipient_display: string;
  frequency: ReportFrequency;
  frequency_display: string;
  weekday: number;
  day_of_month: number;
  starts_on: string;
  label: string;
  is_active: boolean;
}

export interface ReportItemRow {
  id: string;
  cycle_id: string;
  recipient: ReportRecipient;
  recipient_display: string;
  frequency: ReportFrequency;
  frequency_display: string;
  label: string;
  period_start: string;
  period_end: string;
  due_date: string;
  status: ReportItemStatus;
  submitted_at: string | null;
  submitted_by_label: string | null;
  forwarded_at: string | null;
  forwarded_by_label: string | null;
  files: LedFile[];
  note: string;
}

export interface ReportsPayload {
  cycles: ReportCycleRow[];
  items: ReportItemRow[];
}

export interface RouteTutorialLayer {
  layer_name: string;
  kind: string;
  kind_label: string;
  semantic_type: string;
  semantic_label: string;
  status: string;
  status_label: string;
  what: string;
}

export interface RouteTutorial {
  preset: { id: string; name: string; description: string } | null;
  presets: { id: string; name: string; description: string }[];
  srid: number;
  zone_label: string;
  layers: RouteTutorialLayer[];
}

export interface RouteValidation {
  preset_name: string | null;
  [key: string]: unknown;
}

export interface AnalysisProof {
  at: string;
  project: string;
  kind: "title_secured" | "tile" | "letter";
  text: string;
  by: string;
}

export interface AnalysisAttention {
  project: string;
  kind:
    | "letter_overdue" | "parcels_stalled" | "memo_unread" | "tile_overdue" | "report_overdue"
    | "waiting_on_us" | "project_idle";
  text: string;
  ours?: boolean;
}

export interface AnalysisProject {
  leadership_id: string;
  connection_id: string;
  name: string;
  active: boolean;
  titles_secured: number;
  tiles_moved: number;
  entries: number;
  letters_out: number;
  letters_in: number;
  parcels_stalled: number;
  letters_overdue: number;
  titles_pending: number;
}

export interface AnalysisTile {
  project: string;
  label: string;
  side: LedSide | null;
  before: string;
  now: string;
  transitions: string[];
  in_state_days: number;
  due_date: string | null;
}

export interface AnalysisResult {
  contractor: { id: string; name: string };
  window: { since: string; until: string };
  verdict: Record<string, number>;
  proofs: AnalysisProof[];
  attention: AnalysisAttention[];
  projects: AnalysisProject[];
  tiles: AnalysisTile[];
  weeks: { week: string; titles: number; entries: number }[];
}

export interface AnalysisContractor {
  id: string;
  name: string;
  projects: number;
}

/** Polskie nazwy rodzajów wpisów dziennika (LedEventKind + najczęstsze ProjectEventKind). */
export const JOURNAL_KIND_LABEL: Record<string, string> = {
  handed_over: "Projekt przekazany podwykonawcy",
  revoked: "Projekt odebrany podwykonawcy",
  caretaker_changed: "Zmiana opiekuna",
  tile_added: "Kafel dodany",
  tile_removed: "Kafel usunięty",
  tile_edited: "Kafel zmieniony",
  tile_side_changed: "Zmiana strony kafla",
  tile_assigned: "Kafel zlecony",
  tile_state: "Zmiana stanu kafla",
  tile_file: "Plik kafla",
  tile_requested: "Prośba o kafel",
  tile_request_decided: "Prośba o kafel rozstrzygnięta",
  route_uploaded: "Wgrano rysunek trasy",
  title_status_set: "Stan tytułu prawnego",
  title_reported: "Tytuł zgłoszony do potwierdzenia",
  title_confirmed: "Tytuł potwierdzony przez ELEKTIM",
  title_rejected: "Zgłoszenie tytułu odrzucone",
  entry_added: "Wpis w dzienniku działań",
  entry_edited: "Wpis poprawiony",
  entry_withdrawn: "Wpis wycofany",
  register_added: "Wpis w rejestrze spraw",
  register_shared: "Pismo udostępnione firmie",
  register_read: "Pismo przeczytane",
  owner_added: "Dodano stronę",
  owner_edited: "Zmieniono dane strony",
  marital_joined: "Połączono małżeństwo",
  marital_left: "Rozłączono małżeństwo",
  parcel_kw: "Numer KW działki",
  parcel_road: "Droga wewnętrzna",
  institution_added: "Instytucja w katalogu projektu",
  institution_edited: "Instytucja zmieniona",
  memo_added: "Notatka służbowa",
  memo_edited: "Notatka służbowa poprawiona",
  memo_read: "Notatka odczytana",
  report_cycle: "Harmonogram raportów",
  report_submitted: "Raport złożony",
  report_forwarded: "Raport przekazany Zamawiającemu",
  journal_exported: "Eksport dziennika",
  files_copied: "Pliki projektu skopiowane",
  file_added: "Plik dodany",
  file_removed: "Plik usunięty",
  file_hidden: "Widoczność pliku dla firmy",
  folder_changed: "Folder plików",
  project_created: "Utworzenie projektu",
  route_version_created: "Nowa wersja trasy (DXF)",
  route_version_edited: "Edycja wersji trasy",
  route_version_deleted: "Usunięcie wersji trasy",
  route_binding_changed: "Zmiana wariantu obowiązującego",
  parcel_added: "Dodano działkę",
  parcel_entered_route: "Działka weszła pod trasę",
  parcel_left_route: "Działka poza trasą",
  parcel_route_changed: "Zmiana trasy na działce",
  wpw_mail_letter: "WPW — ścieżka listowna",
  parcel_route_review_cleared: "Zmiana trasy — zweryfikowano",
  parcel_status_changed: "Zmiana statusu działki",
  parcel_data_edited: "Edycja danych działki",
  parcel_note: "Notatka do działki",
  owner_access_status_changed: "Zmiana statusu tytułu prawnego",
  owner_access_route_extended: "Tytuł rozciągnięty na aktualną trasę",
  proceeding_status_changed: "Zmiana statusu postępowania",
  proceeding_item: "Pismo w postępowaniu",
  decision_registered: "Zarejestrowano decyzję",
  milestone_changed: "Kamień milowy",
  topic_status_changed: "Zmiana statusu tematu",
};
